/**
 * TalepFiltreleri.jsx — the filter bar of a request list (backlog #458, 2026-10-17; backend #457): quick chips for the kind of technical request
 * (Elektrik / Su / Hava / İnternet), a kW range, the "above the allowed kW" toggle, the request status and (#462) who entered the request. The search box stays with the list
 * (it already has one); this bar is only the electricity-aware part. State lives in the address (talepFiltreleri.js: useTalepFiltreleri).
 *
 * The kW fields apply when you leave the field or press Enter (not on every keystroke: each change asks the server). They are electricity filters, so using
 * one puts the kind on Elektrik. Presentational: the page owns the hook and passes `filtre`, `degistir` and `temizle`.
 *
 * Reusable for the staff request page (#77) when it is built: nothing here is specific to the organizer.
 */
import React, { useState } from "react";
import { Badge, Button, Input, Label } from "reactstrap";
import { useTranslation } from "react-i18next";
import { TALEP_DURUMLARI, TALEP_TURLERI, etkinFiltreSayisi } from "./talepFiltreleri";
import { KAYNAK_FILTRELERI } from "./talepKaynaklari";

/** One kW field: typed text is local, the address changes when the field is left or Enter is pressed. */
function KwAlani({ kimlik, deger, yer, onUygula }) {
  const [yazilan, setYazilan] = useState(null);   // null = show the address's value
  const gosterilen = yazilan ?? deger;
  const uygula = () => {
    if (yazilan !== null && yazilan !== deger) onUygula(yazilan);
    setYazilan(null);
  };
  return (
    <Input type="number" min={0} step="any" bsSize="sm" style={{ width: 96 }} placeholder={yer} value={gosterilen} id={kimlik} data-testid={kimlik}
           onChange={(e) => setYazilan(e.target.value)} onBlur={uygula}
           onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); uygula(); } }} />
  );
}

export default function TalepFiltreleri({ filtre, degistir, temizle }) {
  const { t } = useTranslation("portal");
  const etkin = etkinFiltreSayisi(filtre);
  return (
    <div className="d-flex flex-wrap align-items-center gap-2 mb-2" data-testid="talep-filtreleri">
      <div className="d-flex flex-wrap align-items-center gap-1" role="group" aria-label={t("organizator.talepFiltre.tur")}>
        <Button size="sm" color="primary" outline={filtre.tur !== ""} data-testid="tur-hepsi" onClick={() => degistir({ tur: "" })}>
          {t("organizator.talepFiltre.hepsi")}
        </Button>
        {TALEP_TURLERI.map((tur) => (
          <Button key={tur.kod} size="sm" color="primary" outline={filtre.tur !== tur.kod} active={filtre.tur === tur.kod} data-testid={`tur-${tur.kod}`}
                  onClick={() => degistir({ tur: filtre.tur === tur.kod ? "" : tur.kod })}>
            {t(`organizator.talepFiltre.turler.${tur.kod}`)}
          </Button>
        ))}
      </div>

      <div className="d-flex align-items-center gap-1">
        <Label className="small mb-0 text-muted" for="kw-min">{t("organizator.talepFiltre.kw")}</Label>
        <KwAlani kimlik="kw-min" deger={filtre.kwMin} yer={t("organizator.talepFiltre.enAz")} onUygula={(v) => degistir({ kwMin: v })} />
        <span className="text-muted">–</span>
        <KwAlani kimlik="kw-max" deger={filtre.kwMax} yer={t("organizator.talepFiltre.enCok")} onUygula={(v) => degistir({ kwMax: v })} />
      </div>

      <div className="form-check form-switch mb-0">
        <Input type="switch" role="switch" id="kw-asan" checked={filtre.asan} data-testid="kw-asan" onChange={(e) => degistir({ asan: e.target.checked })} />
        <Label for="kw-asan" className="small mb-0">{t("organizator.talepFiltre.asanlar")}</Label>
      </div>

      <Input type="select" bsSize="sm" style={{ width: 150 }} value={filtre.durum} aria-label={t("organizator.talepFiltre.durum")} data-testid="talep-durum"
             onChange={(e) => degistir({ durum: e.target.value })}>
        <option value="">{t("organizator.talepFiltre.durumHepsi")}</option>
        {TALEP_DURUMLARI.map((d) => <option key={d} value={d}>{t(`organizator.talepFiltre.durumlar.${d}`)}</option>)}
      </Input>

      {/* #462: who entered the request: a company (Katılımcı + Dekoratör), the Katılımcı, the Dekoratör, the organizer or SAKA staff */}
      <Input type="select" bsSize="sm" style={{ width: 170 }} value={filtre.kaynak ?? ""} aria-label={t("organizator.talepFiltre.kaynak")} data-testid="talep-kaynak"
             onChange={(e) => degistir({ kaynak: e.target.value })}>
        <option value="">{t("organizator.talepFiltre.kaynakHepsi")}</option>
        {KAYNAK_FILTRELERI.map((k) => <option key={k} value={k}>{t(`organizator.talepFiltre.kaynaklar.${k}`)}</option>)}
      </Input>

      {etkin > 0 && (
        <Button size="sm" color="link" className="p-0" data-testid="filtreleri-temizle" onClick={temizle}>
          {t("organizator.talepFiltre.temizle")} <Badge color="secondary" pill>{etkin}</Badge>
        </Button>
      )}
    </div>
  );
}
